// src/services/recordingBuffer.js
// ── Pre-recorded class recording buffer (IndexedDB-backed) ──
//
// Purpose (2026-04-23): During a pre-recorded class recording, MediaRecorder
// produces a WebM chunk every 250 ms. The prior implementation accumulated
// those chunks in an in-memory array (`recordedChunksRef`), which has two
// irreducible failure modes on constrained devices:
//
//   1. Memory ceiling — a 12-min recording at 4K (15 Mbps) is ~1.35 GB of
//      live blob data. When Chrome's per-origin blob storage budget is
//      exceeded, new chunks are silently dropped or MediaRecorder stops
//      without firing any observable event. The admin sees a wall-clock
//      timer that keeps ticking, clicks End Class at 12:00, and gets a
//      6-min VOD at Mux. This is exactly the incident that motivated this
//      service (reported 2026-04-23).
//
//   2. Lifetime coupling — chunks live only as long as the LiveBroadcaster
//      component is mounted. Any unmount path (tab navigation, error
//      boundary, accidental close) throws away everything captured.
//
// This service moves the chunk buffer into IndexedDB, which:
//   • Has predictable, managed quota (typically several GB on desktop
//     Chrome). Over-quota fails loudly with QuotaExceededError rather than
//     silently dropping data.
//   • Survives tab reloads. If admin closes the tab mid-recording, the
//     chunks remain on disk and can be recovered on next mount (see
//     listRecordings / getRecordingBlob below — Workouts.js wires the
//     recovery prompt).
//   • Stores Blobs as references to backing storage (not byte copies), so
//     the final `new Blob([chunk1, chunk2, ...])` at stop-time is a
//     composite reference, not a full in-memory reassembly.
//
// Schema (database `mom-recording-buffer`, version 1):
//   • `chunks`   — keyed by auto-increment; each record carries a
//     `classId` index so we can scope queries to one recording session.
//     Records: { classId, sequence, blob, timestamp }.
//   • `metadata` — keyed by classId. One record per recording session.
//     Records: { classId, startedAt, mimeType, chunkCount, lastChunkAt,
//                classTitle, videoBps, audioBps }.
//
// Write semantics:
//   • `appendChunk(classId, blob)` is called from MediaRecorder's
//     ondataavailable. Writes are promise-chained per-classId so in-flight
//     writes never interleave out of order — important because chunk
//     ordering in the final Blob must match capture order exactly (WebM
//     containers are order-sensitive).
//   • Writes to `metadata` (chunkCount + lastChunkAt) are fire-and-forget
//     updates; failures log but don't block the primary chunk write.
//
// Read semantics:
//   • `getRecordingBlob(classId)` reads ALL chunks for a classId ordered
//     by insertion sequence and returns a composite Blob. Safe to call
//     multiple times (idempotent — does not delete).
//   • `clearRecording(classId)` deletes all chunks + metadata for a
//     classId. Called by Workouts.js after successful upload.
//
// Recovery semantics:
//   • `listRecordings()` returns all metadata records. Workouts.js calls
//     this on mount — if any are present, an "unfinished recording found"
//     modal prompts the admin to upload or discard.
//
// Failure behavior:
//   • Quota exceeded: the promise rejects. Caller (LiveBroadcaster) surfaces
//     a banner and halts MediaRecorder to prevent further capture loss.
//   • Transaction aborts: caller retries once; if second attempt fails, the
//     recording is abandoned with a banner.
//   • Browser without IndexedDB: openBuffer() rejects; caller must fall
//     through to a feature-detection path. (All target browsers support
//     IDB; this is defensive only.)

const DB_NAME = 'mom-recording-buffer';
const DB_VERSION = 1;
const STORE_CHUNKS = 'chunks';
const STORE_METADATA = 'metadata';

// ── Per-classId write chains ──
// MediaRecorder delivers chunks from a native thread; back-to-back
// `appendChunk` calls can overlap. IDB transactions serialise themselves,
// but we want deterministic ordering AND the ability to await "all writes
// flushed" at stop-time. Each classId gets its own promise chain; every
// append awaits the prior append's completion before opening its own
// transaction. Lookup is O(1) via a plain object.
const writeChains = {};

let dbPromise = null;

function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        if (typeof indexedDB === 'undefined') {
            reject(new Error('IndexedDB not available in this browser'));
            return;
        }
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_CHUNKS)) {
                // Auto-increment keyPath preserves insertion order globally.
                // classId index lets us fetch all chunks for one session.
                const chunksStore = db.createObjectStore(STORE_CHUNKS, { keyPath: 'id', autoIncrement: true });
                chunksStore.createIndex('classId', 'classId', { unique: false });
            }
            if (!db.objectStoreNames.contains(STORE_METADATA)) {
                db.createObjectStore(STORE_METADATA, { keyPath: 'classId' });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => {
            dbPromise = null; // allow retry on next call
            reject(req.error || new Error('Failed to open recording buffer DB'));
        };
        req.onblocked = () => {
            // Another tab has the DB open at an older version. Since we use a
            // single DB_VERSION and the schema is stable, this is very rare —
            // log and continue waiting. The onsuccess will eventually fire when
            // the other connection closes.
            console.warn('recordingBuffer: DB upgrade blocked by another tab');
        };
    });
    return dbPromise;
}

// Wrap an IDBRequest in a promise. IDB's error semantics require both the
// request's onerror AND the transaction's onabort to be observed.
function reqPromise(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

// ── Public API ──

// Start a new recording session. Clears any existing chunks/metadata for
// `classId` (previous aborted recording on the same class) and writes a
// fresh metadata record. MUST be called before the first appendChunk.
//
// Options.videoBps and audioBps are recorded for forensics only — they help
// the recovery flow estimate how much data is on disk (bytes ≈ bps × elapsed).
export async function startRecording(classId, mimeType, options = {}) {
    if (!classId) throw new Error('startRecording: classId required');
    const db = await openDb();
    // Reset the write chain so a prior session's tail writes don't serialise
    // before this one's. (They should already have drained via finishRecording,
    // but a truly aborted session could leave one queued — reset defensively.)
    writeChains[classId] = Promise.resolve();

    await new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_CHUNKS, STORE_METADATA], 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));

        // Clear any residual chunks for this classId (scoped via the
        // classId index — avoids touching other recordings).
        const chunksStore = tx.objectStore(STORE_CHUNKS);
        const classIndex = chunksStore.index('classId');
        const cursorReq = classIndex.openCursor(IDBKeyRange.only(classId));
        cursorReq.onsuccess = (ev) => {
            const cursor = ev.target.result;
            if (cursor) {
                cursor.delete();
                cursor.continue();
            }
        };

        // Write fresh metadata. title/videoBps/audioBps are optional hints
        // surfaced in the recovery UI.
        const metaStore = tx.objectStore(STORE_METADATA);
        metaStore.put({
            classId,
            classTitle: options.classTitle || '',
            mimeType: mimeType || 'video/webm',
            videoBps: options.videoBps || 0,
            audioBps: options.audioBps || 0,
            startedAt: Date.now(),
            lastChunkAt: Date.now(),
            chunkCount: 0,
        });
    });
}

// Append a single MediaRecorder chunk. Enqueued in the per-classId promise
// chain so successive calls never interleave. Updates metadata.chunkCount
// and metadata.lastChunkAt inside the same transaction (atomicity — if the
// write fails, metadata stays in sync).
//
// Returns the promise of THIS write (chained). Caller does not need to
// await it synchronously — but stopRecording() awaits the whole chain.
export function appendChunk(classId, blob) {
    if (!classId) return Promise.reject(new Error('appendChunk: classId required'));
    if (!blob || blob.size === 0) return Promise.resolve();
    const prior = writeChains[classId] || Promise.resolve();
    const next = prior.then(async () => {
        const db = await openDb();
        return new Promise((resolve, reject) => {
            const tx = db.transaction([STORE_CHUNKS, STORE_METADATA], 'readwrite');
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
            tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));

            const chunksStore = tx.objectStore(STORE_CHUNKS);
            chunksStore.add({ classId, blob, timestamp: Date.now() });

            const metaStore = tx.objectStore(STORE_METADATA);
            const getReq = metaStore.get(classId);
            getReq.onsuccess = () => {
                const record = getReq.result;
                if (record) {
                    record.chunkCount = (record.chunkCount || 0) + 1;
                    record.lastChunkAt = Date.now();
                    metaStore.put(record);
                }
            };
        });
    }).catch((err) => {
        // Don't poison the chain — a single failed write shouldn't block
        // subsequent writes. Re-throw only to the caller of THIS append
        // (via the returned promise). The chain resolves to undefined so
        // the next append proceeds normally.
        console.error(`recordingBuffer.appendChunk failed for class ${classId}:`, err);
        throw err;
    });
    // Replace the chain with a "settled regardless of outcome" promise so
    // a single failure doesn't block everything that follows.
    writeChains[classId] = next.then(() => {}, () => {});
    return next;
}

// Wait for all in-flight chunk writes for this classId to complete. Call
// this before getRecordingBlob to guarantee every delivered chunk is on
// disk. Safe to call even if no writes are pending (resolves immediately).
export function flushChunks(classId) {
    return writeChains[classId] || Promise.resolve();
}

// Read every chunk for a classId (ordered by insertion / sequence) and
// return a composite Blob suitable for XHR.send to Mux's signed upload URL.
//
// IndexedDB stores Blobs as references to backing storage; constructing a
// `new Blob([...])` over those references does NOT copy bytes — the Blob
// is composite and consumed lazily by XHR/fetch. Peak memory therefore
// stays bounded during upload, same as the in-memory array path did.
//
// Cursor iteration via the classId index is required because the primary
// key (auto-increment `id`) mixes chunks from different recordings. Walking
// the index in natural order gives us chunks in insertion order for this
// classId only.
export async function getRecordingBlob(classId) {
    if (!classId) throw new Error('getRecordingBlob: classId required');
    await flushChunks(classId); // ensure all pending writes landed

    const db = await openDb();
    const meta = await new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_METADATA], 'readonly');
        const req = tx.objectStore(STORE_METADATA).get(classId);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
    const mimeType = meta?.mimeType || 'video/webm';

    const chunks = await new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_CHUNKS], 'readonly');
        const store = tx.objectStore(STORE_CHUNKS);
        const idx = store.index('classId');
        const out = [];
        const cursorReq = idx.openCursor(IDBKeyRange.only(classId));
        cursorReq.onsuccess = (ev) => {
            const cursor = ev.target.result;
            if (cursor) {
                out.push(cursor.value);
                cursor.continue();
            } else {
                resolve(out);
            }
        };
        cursorReq.onerror = () => reject(cursorReq.error);
    });

    // Sort by auto-increment id (insertion order) as a belt-and-suspenders —
    // index cursors SHOULD walk insertion order but some implementations
    // order alphabetically on the indexed field, which for our case is the
    // same value across all records and so non-deterministic without a
    // secondary sort.
    chunks.sort((a, b) => a.id - b.id);

    const blobs = chunks.map((c) => c.blob);
    if (blobs.length === 0) return { blob: null, mimeType, chunkCount: 0 };
    const blob = new Blob(blobs, { type: mimeType });
    return { blob, mimeType, chunkCount: blobs.length };
}

// Read just the metadata for a classId (no chunk data). Used by recovery
// UI to show "X min captured at HH:MM" without loading every chunk into
// memory.
export async function getRecordingMetadata(classId) {
    if (!classId) return null;
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_METADATA], 'readonly');
        const req = tx.objectStore(STORE_METADATA).get(classId);
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => reject(req.error);
    });
}

// Return every pending recording's metadata. Used by Workouts.js on mount
// to detect unfinished sessions and prompt for recovery. Cheap: metadata
// records are small (hundreds of bytes) and almost always one at a time.
export async function listRecordings() {
    const db = await openDb();
    return new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_METADATA], 'readonly');
        const req = tx.objectStore(STORE_METADATA).getAll();
        req.onsuccess = () => resolve(req.result || []);
        req.onerror = () => reject(req.error);
    });
}

// Delete ALL chunks + metadata for a classId. Called by Workouts.js once
// the upload to Mux has succeeded (the classRecordings entry or Mux asset
// is the authoritative record from that point forward).
export async function clearRecording(classId) {
    if (!classId) return;
    const db = await openDb();
    // Release the write chain so future appendChunk calls for this classId
    // start fresh.
    delete writeChains[classId];
    await new Promise((resolve, reject) => {
        const tx = db.transaction([STORE_CHUNKS, STORE_METADATA], 'readwrite');
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));

        const chunksStore = tx.objectStore(STORE_CHUNKS);
        const idx = chunksStore.index('classId');
        const cursorReq = idx.openCursor(IDBKeyRange.only(classId));
        cursorReq.onsuccess = (ev) => {
            const cursor = ev.target.result;
            if (cursor) {
                cursor.delete();
                cursor.continue();
            }
        };

        tx.objectStore(STORE_METADATA).delete(classId);
    });
}

// Pre-flight storage estimate. Returned object shape matches
// `navigator.storage.estimate()` plus a convenience `freeBytes` field. If
// the API is not available (older Firefox, very old browsers), returns
// null and the caller proceeds without the pre-flight warning.
//
// Usage: LiveBroadcaster calls this before starting a recording. If
// freeBytes is below a threshold for the estimated recording size (derived
// from tier bitrate × max class duration), we surface a warning banner.
export async function estimateStorage() {
    if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
    try {
        const est = await navigator.storage.estimate();
        const freeBytes = Math.max(0, (est.quota || 0) - (est.usage || 0));
        return { ...est, freeBytes };
    } catch (e) {
        console.warn('recordingBuffer.estimateStorage failed:', e);
        return null;
    }
}
